import React, { useMemo, useCallback } from 'react';
import api from '../services/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';

const AttendeeDashboard = () => {
  const { user } = useAuth();
  const socket = useSocket();

  // ✅ React Query: Fetch attendee sessions
  const {
    data: sessions = [],
    isLoading,
    isError,
    error,
    refetch: refetchSessions,
  } = useApiQuery(
    ['my-sessions'],
    () => api.get('/sessions/my').then(res => res.data),
    {
      staleTime: 30 * 1000, // 30 seconds
      onError: (err) => {
        console.error(err.response?.data?.message || 'Failed to load sessions');
      },
    }
  );

  // ✅ Socket listener for real-time updates
  React.useEffect(() => {
    if (!socket) return;
    const handleNewSession = () => {
      refetchSessions();
    };
    socket.on('sessionCreated', handleNewSession);
    return () => socket.off('sessionCreated', handleNewSession);
  }, [socket, refetchSessions]);

  // ✅ Group sessions by event (memoized)
  const eventData = useMemo(() => {
    const eventMap = new Map();
    
    sessions.forEach(s => {
      const eventId = s.eventId?._id || s.eventId;
      if (!eventId) return;
      
      if (!eventMap.has(eventId)) {
        eventMap.set(eventId, {
          eventName: s.eventId?.name || 'Unknown Event',
          entries: 0,
          exits: 0,
          total: 0,
          lastScan: null,
        });
      }
      
      const entry = eventMap.get(eventId);
      entry.total++;
      if (s.status === 'IN') entry.entries++;
      else if (s.status === 'OUT') entry.exits++;
      
      if (!entry.lastScan || new Date(s.entryTime) > new Date(entry.lastScan)) {
        entry.lastScan = s.entryTime;
      }
    });
    
    return Array.from(eventMap.values());
  }, [sessions]);

  // ✅ Recent sessions (memoized)
  const recentSessions = useMemo(() => {
    return sessions.slice(0, 5);
  }, [sessions]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading your activity...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isError) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error?.response?.data?.message || 'Failed to load sessions'}
          <button
            onClick={() => refetchSessions()}
            className="ml-4 text-red-700 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-[#2b3445]">My Event Dashboard</h1>
      
      {eventData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center border border-[#dae1e7]">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-[#dae1e7]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500">You haven't checked into any events yet.</p>
          <p className="text-sm text-gray-400 mt-1">Check in at an event to see your activity here.</p>
        </div>
      ) : (
        <>
          {/* Event Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventData.map((event, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg shadow-sm border border-[#dae1e7] p-5 border-t-4 border-t-[#1672b9] hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-semibold text-gray-900">{event.eventName}</h3>
                <div className="mt-3 space-y-1 text-sm text-gray-600">
                  <p className="flex justify-between">
                    <span>Entries:</span> 
                    <span className="font-medium text-green-600">{event.entries}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Exits:</span> 
                    <span className="font-medium text-red-500">{event.exits}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Total Scans:</span> 
                    <span className="font-medium">{event.total}</span>
                  </p>
                  <p className="flex justify-between text-xs text-gray-400 border-t border-[#dae1e7] pt-1 mt-1">
                    <span>Last activity:</span>
                    <span>{event.lastScan ? new Date(event.lastScan).toLocaleString() : '—'}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Activity Table */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4 text-[#2b3445]">Recent Activity</h2>
            <div className="bg-white rounded-lg shadow-sm border border-[#dae1e7] overflow-hidden">
              {recentSessions.length === 0 ? (
                <div className="p-6 text-center text-gray-500">No recent activity.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentSessions.map(session => (
                        <tr key={session._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {session.eventId?.name || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(session.entryTime).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              session.status === 'IN' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {session.status === 'IN' ? 'Entry' : 'Exit'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AttendeeDashboard;